import React, { useRef, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { HiOutlineXMark, HiOutlineDevicePhoneMobile, HiOutlineLockClosed, HiOutlineShieldCheck } from "react-icons/hi2"
import API from "../Common/API"

const STEPS = { MOBILE: 1, OTP: 2, PASSWORD: 3, DONE: 4 }

const ForgotPasswordModal = ({ open, onClose }) => {
    const [step, setStep]           = useState(STEPS.MOBILE)
    const [mobile, setMobile]       = useState("")
    const [otp, setOtp]             = useState(["", "", "", "", "", ""])
    const [devOtp, setDevOtp]       = useState(null)   // shown in UI for testing
    const [newPassword, setNewPassword] = useState("")
    const [confirmPassword, setConfirmPassword] = useState("")
    const [loading, setLoading]     = useState(false)
    const [error, setError]         = useState("")
    const [resendCooldown, setResendCooldown] = useState(0)
    const otpRefs = useRef([])
    const cooldownRef = useRef(null)

    const reset = () => {
        setStep(STEPS.MOBILE); setMobile(""); setOtp(["", "", "", "", "", ""])
        setDevOtp(null); setNewPassword(""); setConfirmPassword(""); setError("")
        clearInterval(cooldownRef.current); setResendCooldown(0)
    }

    const close = () => { reset(); onClose() }

    const startCooldown = () => {
        setResendCooldown(60)
        cooldownRef.current = setInterval(() => {
            setResendCooldown((c) => { if (c <= 1) { clearInterval(cooldownRef.current); return 0 } return c - 1 })
        }, 1000)
    }

    const requestOtp = async (mob = mobile) => {
        const trimmed = mob.trim()
        if (trimmed.length < 4) { setError("Enter your mobile number or username"); return }
        setLoading(true); setError("")
        try {
            const res = await API.post("/auth/requestOtp", { mobileNumber: trimmed })
            if (res.status === 200) {
                setDevOtp(res.data?.data?.otp || null)   // show OTP in UI for testing
                setStep(STEPS.OTP)
                startCooldown()
            }
        } catch (err) {
            setError(err?.response?.data?.error || "Failed to send OTP. Please try again.")
        } finally { setLoading(false) }
    }

    const handleOtpChange = (index, value) => {
        if (!/^\d?$/.test(value)) return
        const next = [...otp]; next[index] = value; setOtp(next)
        if (value && index < 5) otpRefs.current[index + 1]?.focus()
    }

    const handleOtpKeyDown = (index, e) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) otpRefs.current[index - 1]?.focus()
    }

    const verifyOtp = () => {
        const code = otp.join("")
        if (code.length < 6) { setError("Enter the complete 6-digit OTP"); return }
        setError(""); setStep(STEPS.PASSWORD)
    }

    const resetPassword = async () => {
        if (!newPassword || newPassword.length < 8) { setError("Password must be at least 8 characters"); return }
        if (newPassword !== confirmPassword) { setError("Passwords do not match"); return }
        setLoading(true); setError("")
        try {
            const res = await API.post("/auth/resetPasswordWithOtp", {
                mobileNumber: mobile.replace(/\D/g, ""),
                otp: otp.join(""),
                newPassword,
                confirmPassword,
            })
            if (res.status === 200) setStep(STEPS.DONE)
        } catch (err) {
            setError(err?.response?.data?.error || "Reset failed. Please try again.")
        } finally { setLoading(false) }
    }

    const stepLabel = { [STEPS.MOBILE]: "Enter Mobile", [STEPS.OTP]: "Verify OTP", [STEPS.PASSWORD]: "New Password", [STEPS.DONE]: "Done" }

    return (
        <AnimatePresence>
            {open && (
                <motion.div
                    className="fixed inset-0 z-[100] flex items-center justify-center px-4"
                    style={{ background: "rgba(0,0,0,0.5)" }}
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    onClick={close}
                >
                    <motion.div
                        className="w-full max-w-md rounded-[28px] bg-white shadow-2xl overflow-hidden"
                        initial={{ scale: 0.92, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.92, opacity: 0, y: 20 }}
                        transition={{ type: "spring", damping: 28, stiffness: 300 }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between border-b border-borderColor px-6 py-4">
                            <div>
                                <p className="text-xs font-bold uppercase tracking-[0.2em] text-primeColor">Account Recovery</p>
                                <h2 className="text-lg font-semibold text-tColor">Forgot Password</h2>
                            </div>
                            <button type="button" onClick={close}
                                className="flex h-8 w-8 items-center justify-center rounded-full border border-borderColor text-slate-400 hover:border-red-300 hover:text-red-500 transition">
                                <HiOutlineXMark className="h-4 w-4" />
                            </button>
                        </div>

                        {/* Step indicator */}
                        <div className="flex items-center gap-0 border-b border-borderColor bg-slate-50">
                            {[STEPS.MOBILE, STEPS.OTP, STEPS.PASSWORD].map((s, i) => (
                                <div key={s} className="flex flex-1 items-center">
                                    <div className={`flex flex-1 flex-col items-center py-3 text-center text-[10px] font-bold uppercase tracking-wider transition
                                        ${step >= s ? "text-primeColor" : "text-slate-300"}`}>
                                        <span className={`mb-1 flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-bold
                                            ${step > s ? "bg-primeColor text-white" : step === s ? "border-2 border-primeColor text-primeColor" : "border-2 border-slate-200 text-slate-300"}`}>
                                            {step > s ? "✓" : i + 1}
                                        </span>
                                        {stepLabel[s]}
                                    </div>
                                    {i < 2 && <div className={`h-px w-4 shrink-0 ${step > s ? "bg-primeColor" : "bg-slate-200"}`} />}
                                </div>
                            ))}
                        </div>

                        <div className="px-6 py-6 space-y-4">

                            {/* ── Step 1: Mobile ── */}
                            {step === STEPS.MOBILE && (
                                <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
                                    <div className="flex items-center gap-3 rounded-2xl bg-blue-50 p-4">
                                        <HiOutlineDevicePhoneMobile className="h-5 w-5 shrink-0 text-blue-600" />
                                        <p className="text-sm text-blue-700">Enter your registered mobile number or username to receive an OTP.</p>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-sm font-semibold text-tColor">Mobile Number or Username</label>
                                        <input
                                            type="text" value={mobile} onChange={(e) => { setMobile(e.target.value); setError("") }}
                                            onKeyDown={(e) => e.key === "Enter" && requestOtp()}
                                            placeholder="Mobile number or username (e.g. admin)"
                                            className="w-full rounded-2xl border border-borderColor bg-slate-50 px-4 py-3 text-sm outline-none focus:border-primeColor focus:bg-white transition"
                                        />
                                    </div>
                                    {error && <p className="rounded-xl bg-red-50 px-4 py-2 text-xs font-semibold text-red-600">{error}</p>}
                                    <button type="button" onClick={() => requestOtp()} disabled={loading}
                                        className="w-full rounded-2xl bg-primeColor py-3 text-sm font-bold text-white transition hover:bg-primeDark disabled:opacity-60">
                                        {loading ? "Sending OTP…" : "Send OTP"}
                                    </button>
                                </motion.div>
                            )}

                            {/* ── Step 2: OTP ── */}
                            {step === STEPS.OTP && (
                                <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
                                    <div className="flex items-center gap-3 rounded-2xl bg-emerald-50 p-4">
                                        <HiOutlineShieldCheck className="h-5 w-5 shrink-0 text-emerald-600" />
                                        <p className="text-sm text-emerald-700">OTP sent for <strong>{mobile}</strong>. Valid for 10 minutes. (Test OTP: <strong>123456</strong>)</p>
                                    </div>

                                    {/* Dev mode: show OTP */}
                                    {devOtp && (
                                        <div className="rounded-2xl border-2 border-dashed border-amber-300 bg-amber-50 px-4 py-3 text-center">
                                            <p className="text-xs font-bold uppercase tracking-wider text-amber-600">Testing Mode — Your OTP</p>
                                            <p className="mt-1 text-3xl font-bold tracking-[0.3em] text-amber-700">{devOtp}</p>
                                            <p className="mt-1 text-xs text-amber-500">(In production, this will be sent via SMS)</p>
                                        </div>
                                    )}

                                    <div className="space-y-2">
                                        <label className="text-sm font-semibold text-tColor">Enter 6-digit OTP</label>
                                        <div className="flex gap-2 justify-center">
                                            {otp.map((digit, i) => (
                                                <input key={i} ref={(el) => (otpRefs.current[i] = el)}
                                                    type="text" inputMode="numeric" maxLength={1} value={digit}
                                                    onChange={(e) => handleOtpChange(i, e.target.value)}
                                                    onKeyDown={(e) => handleOtpKeyDown(i, e)}
                                                    className="h-12 w-10 rounded-xl border-2 border-borderColor bg-slate-50 text-center text-lg font-bold outline-none focus:border-primeColor focus:bg-white transition"
                                                />
                                            ))}
                                        </div>
                                    </div>

                                    {error && <p className="rounded-xl bg-red-50 px-4 py-2 text-xs font-semibold text-red-600">{error}</p>}

                                    <button type="button" onClick={verifyOtp}
                                        className="w-full rounded-2xl bg-primeColor py-3 text-sm font-bold text-white transition hover:bg-primeDark">
                                        Verify OTP
                                    </button>

                                    <div className="text-center text-xs text-tpColor">
                                        {resendCooldown > 0
                                            ? <span>Resend OTP in <strong className="text-tColor">{resendCooldown}s</strong></span>
                                            : <button type="button" onClick={() => requestOtp()} className="font-bold text-primeColor hover:underline">Resend OTP</button>
                                        }
                                    </div>
                                </motion.div>
                            )}

                            {/* ── Step 3: New Password ── */}
                            {step === STEPS.PASSWORD && (
                                <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
                                    <div className="flex items-center gap-3 rounded-2xl bg-violet-50 p-4">
                                        <HiOutlineLockClosed className="h-5 w-5 shrink-0 text-violet-600" />
                                        <p className="text-sm text-violet-700">OTP verified! Set your new password below.</p>
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-sm font-semibold text-tColor">New Password</label>
                                        <input type="password" value={newPassword} onChange={(e) => { setNewPassword(e.target.value); setError("") }}
                                            placeholder="Minimum 8 characters"
                                            className="w-full rounded-2xl border border-borderColor bg-slate-50 px-4 py-3 text-sm outline-none focus:border-primeColor focus:bg-white transition" />
                                    </div>
                                    <div className="space-y-1">
                                        <label className="text-sm font-semibold text-tColor">Confirm Password</label>
                                        <input type="password" value={confirmPassword} onChange={(e) => { setConfirmPassword(e.target.value); setError("") }}
                                            placeholder="Re-enter new password"
                                            onKeyDown={(e) => e.key === "Enter" && resetPassword()}
                                            className="w-full rounded-2xl border border-borderColor bg-slate-50 px-4 py-3 text-sm outline-none focus:border-primeColor focus:bg-white transition" />
                                    </div>
                                    {error && <p className="rounded-xl bg-red-50 px-4 py-2 text-xs font-semibold text-red-600">{error}</p>}
                                    <button type="button" onClick={resetPassword} disabled={loading}
                                        className="w-full rounded-2xl bg-primeColor py-3 text-sm font-bold text-white transition hover:bg-primeDark disabled:opacity-60">
                                        {loading ? "Resetting…" : "Reset Password"}
                                    </button>
                                </motion.div>
                            )}

                            {/* ── Step 4: Done ── */}
                            {step === STEPS.DONE && (
                                <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="py-6 text-center space-y-4">
                                    <div className="flex justify-center">
                                        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100">
                                            <HiOutlineShieldCheck className="h-8 w-8 text-emerald-600" />
                                        </div>
                                    </div>
                                    <div>
                                        <h3 className="text-lg font-semibold text-tColor">Password Reset!</h3>
                                        <p className="mt-2 text-sm text-tpColor">Your password has been updated successfully. You can now log in with your new password.</p>
                                    </div>
                                    <button type="button" onClick={close}
                                        className="w-full rounded-2xl bg-primeColor py-3 text-sm font-bold text-white transition hover:bg-primeDark">
                                        Go to Login
                                    </button>
                                </motion.div>
                            )}

                        </div>
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    )
}

export default ForgotPasswordModal
